import React from 'react';
import { LegalLayout } from './LegalLayout';

export default function PrivacyPage() {
  return (
    <LegalLayout title="Privacy Policy" lastUpdated="9 March 2026">
      <h2>1. Introduction</h2>
      <p>
        This Privacy Policy explains how Bidsentry Ltd ("Company", "we", "us", "our"),
        trading as BidSentry, collects, uses, stores, and protects your personal data when you
        use the BidSentry platform ("Platform", "Service").
      </p>
      <p>
        We are registered in England and Wales (Company No: 17096278).
        Our registered office is at 124 City Road, London, EC1V 2NX, United Kingdom.
      </p>
      <p>
        We are committed to protecting your privacy and processing your personal data in accordance
        with the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018.
      </p>

      <h2>2. Data Controller</h2>
      <p>
        Bidsentry Ltd is the data controller for the personal data processed through the Platform.
        For data protection enquiries, contact us at: <strong>privacy@bidsentry.io</strong>.
      </p>

      <h2>3. Personal Data We Collect</h2>
      <p>We collect the following categories of personal data:</p>

      <h3>3.1 Data You Provide</h3>
      <ul>
        <li><strong>Account information:</strong> Name, email address, company name, job title.</li>
        <li><strong>Bid and commercial data:</strong> Tender documents, cost breakdowns, margin analysis data, and related commercial information you upload for analysis.</li>
        <li><strong>Communications:</strong> Messages sent through the Platform, including support tickets and feedback.</li>
        <li><strong>Payment information:</strong> Billing details processed through our third-party payment provider (we do not store card details directly).</li>
      </ul>

      <h3>3.2 Data We Collect Automatically</h3>
      <ul>
        <li><strong>Usage data:</strong> Pages visited, features used, session duration, clickstream data.</li>
        <li><strong>Device and technical data:</strong> IP address, browser type and version, operating system, device identifiers.</li>
        <li><strong>Cookies:</strong> See our <a href="/cookies" className="text-orange-600 hover:text-orange-700">Cookie Policy</a> for full details.</li>
      </ul>

      <h2>4. Legal Basis for Processing</h2>
      <p>We process your personal data under the following legal bases:</p>
      <ul>
        <li><strong>Contract performance:</strong> Processing necessary to deliver the Service you have signed up for (Article 6(1)(b) UK GDPR).</li>
        <li><strong>Legitimate interests:</strong> Platform improvement, fraud prevention, and business communications where we have a legitimate interest that does not override your rights (Article 6(1)(f)).</li>
        <li><strong>Consent:</strong> Where you have given explicit consent, such as for marketing communications or optional analytics (Article 6(1)(a)).</li>
        <li><strong>Legal obligation:</strong> Where processing is required to comply with UK law (Article 6(1)(c)).</li>
      </ul>

      <h2>5. How We Use Your Data</h2>
      <p>We use your personal data to:</p>
      <ul>
        <li>Provide, operate, and maintain the Platform and its features.</li>
        <li>Process and analyse bid data to generate commercial insights and AI-assisted commentary.</li>
        <li>Manage your account, billing, and subscription.</li>
        <li>Communicate with you regarding your account, support requests, and service updates.</li>
        <li>Improve the Platform through aggregated, anonymised usage analytics.</li>
        <li>Detect and prevent fraud, abuse, and security incidents.</li>
        <li>Comply with legal and regulatory obligations.</li>
      </ul>

      <h2>6. AI and Automated Processing</h2>
      <p>
        The Platform uses AI models and deterministic algorithms to analyse bid data, generate commercial
        insights, classify support tickets, and draft communications. These processes involve automated
        decision-making in the sense that they generate outputs without direct human intervention at the
        point of generation.
      </p>
      <p>
        However, all AI-generated outputs are presented as assistive information. No automated decision
        with legal or similarly significant effects is made without human review.
        All automated outreach and AI-drafted communications are subject to human approval before delivery.
      </p>

      <h2>7. Data Sharing and Third Parties</h2>
      <p>We share personal data only in the following circumstances:</p>
      <ul>
        <li><strong>Service providers:</strong> Cloud hosting (managed infrastructure), email delivery (Resend), payment processing, and AI model providers (Anthropic) — all acting as data processors under contract.</li>
        <li><strong>Legal requirements:</strong> Where required by law, court order, or regulatory authority.</li>
        <li><strong>Business transfers:</strong> In the event of a merger, acquisition, or sale of assets, personal data may be transferred as part of that transaction.</li>
      </ul>
      <p>
        We do not sell your personal data to third parties.
        We do not share your data with advertising networks.
      </p>

      <h2>8. International Data Transfers</h2>
      <p>
        Your data is primarily stored and processed within the United Kingdom and European Economic Area.
        Where data is transferred to processors outside these jurisdictions (e.g., AI model providers
        operating in the United States), we ensure appropriate safeguards are in place, including
        Standard Contractual Clauses (SCCs) or equivalent mechanisms approved under UK GDPR.
      </p>

      <h2>9. Data Retention</h2>
      <p>We retain your personal data for as long as necessary to fulfil the purposes for which it was collected:</p>
      <ul>
        <li><strong>Account data:</strong> Retained for the duration of your account plus 12 months after closure.</li>
        <li><strong>Bid and analysis data:</strong> Retained for the duration of your subscription. On account closure, bid data is deleted within 90 days unless a longer retention is required by law.</li>
        <li><strong>Support data:</strong> Retained for 24 months after resolution.</li>
        <li><strong>Legal consent records:</strong> Retained for 6 years for compliance purposes.</li>
        <li><strong>Payment records:</strong> Retained for 7 years in accordance with HMRC requirements.</li>
      </ul>

      <h2>10. Your Rights</h2>
      <p>Under UK GDPR, you have the following rights:</p>
      <ul>
        <li><strong>Right of access:</strong> Request a copy of the personal data we hold about you.</li>
        <li><strong>Right to rectification:</strong> Request correction of inaccurate personal data.</li>
        <li><strong>Right to erasure:</strong> Request deletion of your personal data (subject to legal exceptions).</li>
        <li><strong>Right to restrict processing:</strong> Request limitation of processing in certain circumstances.</li>
        <li><strong>Right to data portability:</strong> Receive your personal data in a structured, machine-readable format.</li>
        <li><strong>Right to object:</strong> Object to processing based on legitimate interests or for direct marketing.</li>
        <li><strong>Right to withdraw consent:</strong> Where processing is based on consent, you may withdraw it at any time.</li>
      </ul>
      <p>
        To exercise any of these rights, visit our{' '}
        <a href="/account/data-request" className="text-orange-600 hover:text-orange-700">Data Request</a> page
        or contact us at <strong>privacy@bidsentry.io</strong>.
        We will respond within one month of receiving your request.
      </p>

      <h2>11. Data Security</h2>
      <p>
        We implement appropriate technical and organisational measures to protect your personal data,
        including encryption in transit (TLS) and at rest, access controls, regular security assessments,
        and employee training. While no system is completely secure, we take all reasonable steps to
        safeguard your data.
      </p>

      <h2>12. Children's Data</h2>
      <p>
        The Platform is not intended for individuals under the age of 18.
        We do not knowingly collect personal data from children.
      </p>

      <h2>13. Changes to This Policy</h2>
      <p>
        We may update this Privacy Policy from time to time. Material changes will be communicated via email
        or a prominent notice within the Platform. The "Last Updated" date at the top of this page reflects
        the most recent revision.
      </p>

      <h2>14. Complaints</h2>
      <p>
        If you are dissatisfied with how we handle your personal data, you have the right to lodge a complaint
        with the Information Commissioner's Office (ICO):
      </p>
      <p>
        Information Commissioner's Office<br />
        Wycliffe House, Water Lane, Wilmslow, Cheshire SK9 5AF<br />
        <strong>Website:</strong> <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:text-orange-700">ico.org.uk</a><br />
        <strong>Helpline:</strong> 0303 123 1113
      </p>

      <h2>15. Contact</h2>
      <p>
        For data protection enquiries, contact us at:<br />
        <strong>Email:</strong> privacy@bidsentry.io<br />
        <strong>Company:</strong> Bidsentry Ltd<br />
        <strong>Registered Office:</strong> 124 City Road, London, EC1V 2NX, United Kingdom
      </p>

      <h2>Data Controller</h2>
      <p>
        Bidsentry Ltd<br />
        124 City Road<br />
        London<br />
        EC1V 2NX<br />
        United Kingdom
      </p>
    </LegalLayout>
  );
}
